import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	total: 0,
	document: null,
	loading: false,
	error: null,
};

// Load employee documents: admin = employee/GetAllEmployeeDocumentsList; user = employee/GetEmployeeDocumentList?EmployeeId=X
export const loadAllEmployeeDocument = createAsyncThunk(
	"employeeDocument/loadAllEmployeeDocument",
	async ({ mode = "admin", employeeId } = {}) => {
		try {
			if (mode === "user" && employeeId != null && employeeId !== "") {
				const { data } = await axios.get(
					`employee/GetEmployeeDocumentList?EmployeeId=${encodeURIComponent(employeeId)}`
				);
				return data;
			}
			const { data } = await axios.get("employee/GetAllEmployeeDocumentsList");
			return data;
		} catch (error) {
			return { list: [], total: 0 };
		}
	}
);

// Add employee document - POST employee/EmployeeDocumentSetup (EmployeeDocumentId = 0)
export const addEmployeeDocument = createAsyncThunk(
	"employeeDocument/addEmployeeDocument",
	async (formData) => {
		try {
			const { data } = await axios({
				method: "post",
				headers: { Accept: "application/json" },
				url: "employee/EmployeeDocumentSetup",
				data: formData,
			});
			toast.success("Employee Document Added Successfully");
			return { data, message: "success" };
		} catch (error) {
			const msg = error.response?.data?.message || error.response?.data?.error || "Error in adding document";
			toast.error(msg);
			return { message: "error" };
		}
	}
);

// Update employee document - POST employee/EmployeeDocumentSetup (EmployeeDocumentId = id)
export const updateEmployeeDocument = createAsyncThunk(
	"employeeDocument/updateEmployeeDocument",
	async ({ id, formData }) => {
		try {
			if (formData.set) formData.set("EmployeeDocumentId", String(id));
			else formData.append("EmployeeDocumentId", String(id));
			const { data } = await axios({
				method: "post",
				headers: { Accept: "application/json" },
				url: "employee/EmployeeDocumentSetup",
				data: formData,
			});
			toast.success("Employee Document Updated");
			return { data, message: "success" };
		} catch (error) {
			const msg = error.response?.data?.message || error.response?.data?.error || "Error in updating document";
			toast.error(msg);
			return { message: "error" };
		}
	}
);

// Delete employee document
export const deleteEmployeeDocument = createAsyncThunk(
	"employeeDocument/deleteEmployeeDocument",
	async (id) => {
		try {
			const { data } = await axios({
				method: "delete",
				url: `employee-document/${id}`,
			});
			toast.success("Employee Document Deleted");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in deleting document, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Download employee document
export const downloadEmployeeDocument = createAsyncThunk(
	"employeeDocument/downloadEmployeeDocument",
	async ({ id, fileName }) => {
		try {
			const { data } = await axios({
				method: "get",
				url: `employee-document/${id}/download`,
				responseType: "blob",
			});
			const url = window.URL.createObjectURL(new Blob([data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", fileName || "document.pdf");
			document.body.appendChild(link);
			link.click();
			link.remove();
			toast.success("Document Downloaded");
			return {
				message: "success",
			};
		} catch (error) {
			toast.error("Error in downloading document, try again!");
			return {
				message: "error",
			};
		}
	}
);

// Count employee documents
export const countEmployeeDocument = createAsyncThunk(
	"employeeDocument/countEmployeeDocument",
	async () => {
		try {
			const { data } = await axios.get(`employee-document/count`);
			return data;
		} catch (error) {
			// console.log(error.message);
			return { total: 0 };
		}
	}
);

const employeeDocumentSlice = createSlice({
	name: "employeeDocument",
	initialState,
	reducers: {
		clearEmployeeDocument: (state) => {
			state.document = null;
		},
	},
	extraReducers: (builder) => {
		// Load all employee documents
		builder.addCase(loadAllEmployeeDocument.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(loadAllEmployeeDocument.fulfilled, (state, action) => {
			state.loading = false;
			const p = action.payload;
			const arr = Array.isArray(p)
				? p
				: Array.isArray(p?.data)
				? p.data
				: Array.isArray(p?.list)
				? p.list
				: [];
			state.list = arr;
			state.total = p?.total ?? arr.length;
		});
		builder.addCase(loadAllEmployeeDocument.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Add employee document
		builder.addCase(addEmployeeDocument.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addEmployeeDocument.fulfilled, (state, action) => {
			state.loading = false;
			if (action.payload.message === "success") {
				// Optionally refresh the list
			}
		});
		builder.addCase(addEmployeeDocument.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Update employee document
		builder.addCase(updateEmployeeDocument.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(updateEmployeeDocument.fulfilled, (state, action) => {
			state.loading = false;
		});
		builder.addCase(updateEmployeeDocument.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error.message;
		});

		// Delete employee document
		builder.addCase(deleteEmployeeDocument.fulfilled, (state) => {
			// Optionally refresh the list
		});

		// Count employee documents
		builder.addCase(countEmployeeDocument.fulfilled, (state, action) => {
			state.total = action.payload?.total || 0;
		});
	},
});

export default employeeDocumentSlice.reducer;

